<template>
  <div
    :id="groupId"
    ref="radioGroupRef"
    :class="[
      ns.b('group'),
      ns.is('vertical', vertical),
      ns.is('segment', segment),
      ns.bm('group', size),
    ]"
    role="radiogroup"
    data-test-name="radio-group"
    data-test-variant=""
    data-test-state=""
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  toRefs,
} from 'vue'
import { UPDATE_MODEL_EVENT } from '@bigin/constants'
import { radioGroupKey } from '@bigin/tokens'
import {
  useFormItem,
  useFormItemInputId,
  useId,
  useNamespace,
} from '@bigin/hooks'
import { radioGroupEmits, radioGroupProps } from './radio-group'
import type { RadioGroupProps } from '..'

defineOptions({
  name: 'BRadioGroup',
})

const props = defineProps(radioGroupProps)
const emit = defineEmits(radioGroupEmits)

const ns = useNamespace('radio')
const radioId = useId()
const radioGroupRef = ref<HTMLDivElement>()
const { formItem } = useFormItem()
const { inputId: groupId } = useFormItemInputId(props, {
  formItemContext: formItem,
})

const changeEvent = (value: RadioGroupProps['modelValue']) => {
  emit(UPDATE_MODEL_EVENT, value)
  nextTick(() => emit('change', value))
}

onMounted(() => {
  const radios =
    radioGroupRef.value!.querySelectorAll<HTMLInputElement>('[type=radio]')
  const firstLabel = radios[0]
  if (!Array.from(radios).some((radio) => radio.checked) && firstLabel) {
    firstLabel.tabIndex = 0
  }
})

const name = computed(() => {
  return props.name || radioId.value
})

provide(
  radioGroupKey,
  reactive({
    ...toRefs(props),
    changeEvent,
    name,
  })
)
</script>
